import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from './SEO';
import { ToolPageExtras } from '../seo/ToolPageExtras';
import { useAdRedirect, AdToast } from '../hooks/useAdRedirect';
import { AdUnit } from './AdUnit';
import { 
  Timer, 
  ArrowLeft, 
  Play, 
  Pause, 
  RotateCcw, 
  BellRing, 
  CheckCircle2, 
  Sparkles 
} from 'lucide-react';

export const CountdownTimer = () => {
  const { withAd, toast } = useAdRedirect();
  const [inputHours, setInputHours] = useState('0');
  const [inputMinutes, setInputMinutes] = useState('5');
  const [inputSeconds, setInputSeconds] = useState('0');

  const [remainingTime, setRemainingTime] = useState(300); // in seconds
  const [totalTime, setTotalTime] = useState(300);
  const [running, setRunning] = useState(false);
  const [alarmPlaying, setAlarmPlaying] = useState(false);

  // Audio Context simulation
  const playSimulatedAlarm = () => {
    setAlarmPlaying(true);
    try {
      // browser web audio api
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime); // A5 note
      gain.gain.setValueAtTime(0.5, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 1.5);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 1.5);
    } catch(e) {
      console.log('AudioContext not supported');
    }
  };

  useEffect(() => {
    let timer;
    if (running && remainingTime > 0) {
      timer = setInterval(() => {
        setRemainingTime(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            setRunning(false);
            playSimulatedAlarm();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [running, remainingTime]);

  const setPreset = (sec) => {
    setRunning(false);
    setRemainingTime(sec);
    setTotalTime(sec);

    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;

    setInputHours(h.toString());
    setInputMinutes(m.toString());
    setInputSeconds(s.toString());
  };

  const startCustom = () => {
    let h = parseInt(inputHours || 0);
    let m = parseInt(inputMinutes || 0);
    let s = parseInt(inputSeconds || 0);

    let total = (h * 3600) + (m * 60) + s;
    if (total <= 0) return;

    setRemainingTime(total);
    setTotalTime(total);
    setRunning(true);
    setAlarmPlaying(false);
  };

  const handlePause = () => {
    setRunning(false);
  };

  const handleReset = () => {
    setRunning(false);
    setAlarmPlaying(false);
    setRemainingTime(totalTime);
  };

  // Format display HH:MM:SS
  const formatDisp = (sec) => {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;

    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const progPercent = totalTime > 0 ? ((totalTime - remainingTime) / totalTime) * 100 : 0;

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%', paddingBottom: '60px' }}>
      <SEO 
        title="Free Online Countdown Timer & Alarm" 
        description="Set custom countdown timers for study sessions, cooking, or productivity. Features visual progress rings and alarm audio notifications."
        keywords="countdown timer, productivity timer, online alarm, study timer, 5 minute timer, pomodoro timer online"
      />
      
      {/* Breadcrumbs Navigation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px', fontSize: '0.9rem' }}>
        <Link to="/" style={{ color: 'var(--text-secondary)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>Countdown Timer</span>
      </div>

      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 8px 0' }}>
          Online Countdown Timer & Productivity Alarm
        </h1>
        <p style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', margin: 0 }}>
          Set high-precision countdown alarms for Pomodoro study intervals, baking schedules, and workout breaks.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '32px' }} className="tool-grid-split">
        
        {/* Left Side: Setup */}
        <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)', alignSelf: 'start' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Timer size={18} style={{ color: 'var(--primary)' }} /> Timer Setup
            </h3>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Quick Presets */}
            <div>
              <label style={{ display: 'block', marginBottom: '10px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>Quick Presets</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' }}>
                {[
                  { label: '1m', sec: 60 },
                  { label: '5m', sec: 300 },
                  { label: '15m', sec: 900 },
                  { label: '30m', sec: 1800 },
                  { label: '1h', sec: 3600 }
                ].map(p => (
                  <button
                    key={p.label}
                    onClick={() => setPreset(p.sec)}
                    style={{ padding: '12px 8px', borderRadius: '10px', border: 'none', background: totalTime === p.sec ? 'var(--primary)' : 'rgba(15, 23, 42, 0.04)', color: totalTime === p.sec ? '#ffffff' : 'var(--text-primary)', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', transition: 'var(--transition-smooth)' }}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Input Fields */}
            <div>
              <label style={{ display: 'block', marginBottom: '10px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>Custom Interval (Hours / Mins / Secs)</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                <div>
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px', display: 'block' }}>Hours</span>
                  <input 
                    type="number" 
                    min="0" 
                    max="99" 
                    value={inputHours} 
                    onChange={(e) => setInputHours(e.target.value)}
                    style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '1.1rem', fontWeight: 700, textAlign: 'center', outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
                  />
                </div>

                <div>
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px', display: 'block' }}>Minutes</span>
                  <input 
                    type="number" 
                    min="0" 
                    max="59" 
                    value={inputMinutes} 
                    onChange={(e) => setInputMinutes(e.target.value)}
                    style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '1.1rem', fontWeight: 700, textAlign: 'center', outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
                  />
                </div>

                <div>
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px', display: 'block' }}>Seconds</span>
                  <input 
                    type="number" 
                    min="0" 
                    max="59" 
                    value={inputSeconds} 
                    onChange={(e) => setInputSeconds(e.target.value)}
                    style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '1.1rem', fontWeight: 700, textAlign: 'center', outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
                  />
                </div>
              </div>
            </div>

            <button
              onClick={startCustom}
              style={{ width: '100%', padding: '16px', borderRadius: '14px', border: 'none', background: 'var(--primary)', color: '#ffffff', fontSize: '1.05rem', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'var(--transition-smooth)' }}
            >
              <Play size={20} /> Set & Start Custom Timer
            </button>
          </div>
        </div>

        {/* Right Side: Active Countdown Dashboard */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '36px 24px', boxShadow: 'var(--shadow-sm)', textAlign: 'center' }}>
            
            {alarmPlaying ? (
              <div style={{ background: 'rgba(239, 68, 68, 0.1)', border: '2px solid #ef4444', padding: '36px', borderRadius: '20px', color: '#ef4444', animation: 'pulse 1s infinite' }}>
                <BellRing size={56} style={{ marginBottom: '16px' }} />
                <h3 style={{ fontSize: '2rem', fontWeight: 900, margin: '0 0 10px 0' }}>TIME IS UP!</h3>
                <p style={{ fontSize: '1rem', margin: '0 0 24px 0', color: 'var(--text-primary)' }}>Your timer interval has fully elapsed.</p>
                <button
                  onClick={handleReset}
                  style={{ background: '#ef4444', color: '#ffffff', border: 'none', padding: '14px 32px', borderRadius: '14px', fontSize: '1rem', fontWeight: 800, cursor: 'pointer' }}
                >
                  Silence Alarm & Reset
                </button>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '16px', letterSpacing: '1px' }}>
                  Countdown Active
                </div>

                {/* Big Time Display */}
                <div style={{ fontSize: '5rem', fontWeight: 900, fontFamily: 'monospace', color: 'var(--text-primary)', marginBottom: '28px', lineHeight: 1 }}>
                  {formatDisp(remainingTime)}
                </div>

                {/* Progress Bar */}
                <div style={{ width: '100%', height: '12px', background: 'var(--bg-secondary)', borderRadius: '6px', overflow: 'hidden', marginBottom: '36px' }}>
                  <div style={{ width: `${progPercent}%`, height: '100%', background: 'var(--primary)', transition: 'width 1s linear' }}></div>
                </div>

                {/* Control Buttons */}
                <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
                  {running ? (
                    <button
                      onClick={handlePause}
                      style={{ padding: '14px 32px', borderRadius: '14px', border: 'none', background: '#f59e0b', color: '#ffffff', fontSize: '1rem', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      <Pause size={18} /> Pause
                    </button>
                  ) : (
                    <button
                      onClick={() => setRunning(true)}
                      disabled={remainingTime <= 0}
                      style={{ padding: '14px 32px', borderRadius: '14px', border: 'none', background: remainingTime <= 0 ? '#cbd5e1' : 'var(--primary)', color: '#ffffff', fontSize: '1rem', fontWeight: 800, cursor: remainingTime <= 0 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      <Play size={18} /> Resume
                    </button>
                  )}

                  <button
                    onClick={handleReset}
                    style={{ padding: '14px 32px', borderRadius: '14px', border: '1px solid rgba(239, 68, 68, 0.3)', background: 'transparent', color: '#ef4444', fontSize: '1rem', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    <RotateCcw size={18} /> Reset
                  </button>
                </div>
              </div>
            )}

            <div style={{ marginTop: '36px' }}>
              <AdToast message={toast} />
              <button 
                onClick={withAd('countdown-btn', () => alert("Productivity session logged."))}
                style={{ background: 'rgba(15, 23, 42, 0.04)', color: 'var(--text-secondary)', border: 'none', padding: '8px 16px', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer' }}
              >
                Log Pomodoro Session
              </button>
            </div>
          </div>

        </div>

      </div>

      <style>{`
        @media (min-width: 992px) {
          .tool-grid-split { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>

      <AdUnit />
      <ToolPageExtras toolId="countdown-timer" />
    </div>
  );
};

export default CountdownTimer;
